import React, { useEffect, useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  Box,
} from "@mui/material";

const DisplayGeneralChurchHistory = () => {
  const API_URL = process.env.REACT_APP_API_URL;

  const [eras, setEras] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [selectedEra, setSelectedEra] = useState(null);
  const [formData, setFormData] = useState({});

  const [openView, setOpenView] = useState(false);
  const [openEdit, setOpenEdit] = useState(false);
  const [openDelete, setOpenDelete] = useState(false);

  // ================= FETCH =================
  const fetchEras = async () => {
    try {
      const res = await fetch(`${API_URL}/enterprise/general-history`);
      const result = await res.json();

      if (result.success) {
        setEras(result.data || []);
      }
    } catch (err) {
      console.error("Fetch error:", err);
    }
  };

  useEffect(() => {
    fetchEras();
  }, []);

  // ================= PAGINATION =================
  const handlePageChange = (_, newPage) => setPage(newPage);

  const handleRowsPerPageChange = (e) => {
    setRowsPerPage(parseInt(e.target.value, 10));
    setPage(0);
  };

  // ================= VIEW =================
  const handleView = async (era) => {
    try {
      const res = await fetch(
        `${API_URL}/enterprise/general-history/${era.id}`,
      );
      const result = await res.json();

      if (result.success) {
        setSelectedEra(result.data);
        setOpenView(true);
      }
    } catch (err) {
      console.error(err);
    }
  };

  // ================= EDIT =================
  const handleEdit = async (era) => {
    try {
      const res = await fetch(
        `${API_URL}/enterprise/general-history/${era.id}`,
      );
      const result = await res.json();

      if (result.success) {
        const data = result.data;
        setSelectedEra(data);

        setFormData({
          title: data.title || "",
          titleAm: data.titleAm || "",
          summary: data.summary || "",
          summaryAm: data.summaryAm || "",
          detail: data.detail || "",
          detailAm: data.detailAm || "",
          keyPeople: data.keyPeople || "",
          keyEvents: data.keyEvents || "",
          accentColor: data.accentColor || "",
          sortOrder: data.sortOrder || 0,
        });

        setOpenEdit(true);
      }
    } catch (err) {
      console.error(err);
    }
  };

  // ================= DELETE =================
  const handleDelete = (era) => {
    setSelectedEra(era);
    setOpenDelete(true);
  };

  const confirmDelete = async () => {
    try {
      await fetch(`${API_URL}/enterprise/general-history/${selectedEra.id}`, {
        method: "DELETE",
      });

      setEras((prev) => prev.filter((e) => e.id !== selectedEra.id));
    } catch (err) {
      console.error(err);
    } finally {
      setOpenDelete(false);
    }
  };

  // ================= UPDATE =================
  const handleUpdate = async () => {
    try {
      const res = await fetch(
        `${API_URL}/enterprise/general-history/${selectedEra.id}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(formData),
        },
      );

      if (res.ok) {
        fetchEras();
      }
    } catch (err) {
      console.error("Update error:", err);
    } finally {
      setOpenEdit(false);
    }
  };

  // ================= INPUT =================
  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  return (
    <>
      {/* ================= TABLE ================= */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Title</TableCell>
              <TableCell>Title (Am)</TableCell>
              <TableCell>Era Code</TableCell>
              <TableCell>Period</TableCell>
              <TableCell>People</TableCell>
              <TableCell>Events</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {eras
              .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              .map((era) => (
                <TableRow key={era.id}>
                  <TableCell>{era.title}</TableCell>
                  <TableCell>{era.titleAm}</TableCell>
                  <TableCell>{era.eraCode}</TableCell>
                  <TableCell>
                    {era.periodStart} - {era.periodEnd}
                  </TableCell>
                  <TableCell>{era.keyPeople}</TableCell>
                  <TableCell>{era.keyEvents}</TableCell>

                  <TableCell
                    sx={{ display: "flex", flexDirection: "column", gap: 1 }}
                  >
                    <Button
                      size="small"
                      variant="contained"
                      onClick={() => handleEdit(era)}
                    >
                      Edit
                    </Button>

                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => handleView(era)}
                    >
                      View
                    </Button>

                    <Button
                      size="small"
                      color="error"
                      variant="contained"
                      onClick={() => handleDelete(era)}
                    >
                      Delete
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>

        <TablePagination
          component="div"
          count={eras.length}
          page={page}
          rowsPerPage={rowsPerPage}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleRowsPerPageChange}
        />
      </TableContainer>

      {/* ================= VIEW DIALOG ================= */}
      <Dialog open={openView} onClose={() => setOpenView(false)} fullWidth>
        <DialogTitle>Era Details</DialogTitle>
        <DialogContent>
          <Typography>
            <b>Title:</b> {selectedEra?.title}
          </Typography>
          <Typography>
            <b>Title (Am):</b> {selectedEra?.titleAm}
          </Typography>
          <Typography>
            <b>Period:</b> {selectedEra?.periodStart} - {selectedEra?.periodEnd}
          </Typography>
          <Typography>
            <b>Summary:</b> {selectedEra?.summary}
          </Typography>
          <Typography>
            <b>Key People:</b> {selectedEra?.keyPeople}
          </Typography>
          <Typography>
            <b>Key Events:</b> {selectedEra?.keyEvents}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenView(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* ================= EDIT DIALOG ================= */}
      <Dialog
        open={openEdit}
        onClose={() => setOpenEdit(false)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>Edit Era</DialogTitle>

        <DialogContent>
          <TextField
            fullWidth
            margin="dense"
            label="Title"
            name="title"
            value={formData.title || ""}
            onChange={handleChange}
          />

          <TextField
            fullWidth
            margin="dense"
            label="Title Am"
            name="titleAm"
            value={formData.titleAm || ""}
            onChange={handleChange}
          />

          <TextField
            fullWidth
            margin="dense"
            label="Summary"
            name="summary"
            value={formData.summary || ""}
            onChange={handleChange}
            multiline
          />

          <TextField
            fullWidth
            margin="dense"
            label="Summary Am"
            name="summaryAm"
            value={formData.summaryAm || ""}
            onChange={handleChange}
            multiline
          />

          <TextField
            fullWidth
            margin="dense"
            label="Key People"
            name="keyPeople"
            value={formData.keyPeople || ""}
            onChange={handleChange}
          />

          <TextField
            fullWidth
            margin="dense"
            label="Key Events"
            name="keyEvents"
            value={formData.keyEvents || ""}
            onChange={handleChange}
          />

          <TextField
            fullWidth
            margin="dense"
            label="Accent Color"
            name="accentColor"
            value={formData.accentColor || ""}
            onChange={handleChange}
          />

          <TextField
            fullWidth
            margin="dense"
            label="Sort Order"
            name="sortOrder"
            type="number"
            value={formData.sortOrder || 0}
            onChange={handleChange}
          />
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setOpenEdit(false)}>Cancel</Button>
          <Button onClick={handleUpdate} variant="contained">
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* ================= DELETE DIALOG ================= */}
      <Dialog open={openDelete} onClose={() => setOpenDelete(false)}>
        <DialogTitle>Delete Era</DialogTitle>
        <DialogContent>
          Delete <b>{selectedEra?.title}</b> ?
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDelete(false)}>Cancel</Button>
          <Button color="error" onClick={confirmDelete}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default DisplayGeneralChurchHistory;
